import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Target, 
  Award, 
  TrendingUp,
  CheckCircle,
  Globe,
  Clock,
  Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockCompanyInfo, mockStats } from '../data/mock';

const AboutPage = () => {
  // Updated team members
  const teamMembers = [
    {
      id: 1,
      name: "Daniel Berg",
      position: "Founder & Managing Director",
      bio: "I lead the organization with over 15 years of experience in freight audit, transportation spend management, and data-driven optimization for global shippers.",
      expertise: ["Financial Operations", "Business Strategy", "Client Relations"]
    },
    {
      id: 2,
      name: "Filippo Pigatto",
      position: "Founder & Head of Origination", 
      bio: "Supply Chain professional turned into a Commodity Trader and Entrepreneur. I leverage data in everything I do, so I created PBX to help your business do the same.",
      expertise: ["Invoice Auditing", "Process Optimization", "Team Leadership"]
    }
  ];
  const values = [
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: "Reliability",
      description: "We deliver consistent, accurate results that our clients can depend on, with self-improving accuracy across all our services."
    },
    {
      icon: <Users className="w-8 h-8 text-teal-600" />,
      title: "Partnership",
      description: "We work as an extension of your team, understanding your business deeply to provide tailored solutions."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Innovation",
      description: "We continuously improve our processes and technology to deliver better outcomes for our clients."
    },
    {
      icon: <Award className="w-8 h-8 text-teal-600" />,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from client service to operational delivery."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
              About PBX Solutions
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Data Management Solutions to Elevate your Business
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We are a team of experienced professionals dedicated to helping import/export and 
              manufacturing companies optimize their data management, invoice auditing, and payable processes.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600">{mockStats.yearsInBusiness}</div>
                <div className="text-gray-600">Years in Business</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600">{mockCompanyInfo.accuracyRate}</div>
                <div className="text-gray-600">Accuracy Rate</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">Global Operations</div>
                <div className="text-gray-600">Localized Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                To empower import/export and manufacturing companies by providing exceptional 
                back-office support services that drive efficiency, accuracy, and cost savings, 
                allowing our clients to focus on their core business objectives.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">Deliver self-improving accuracy in all services</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">Provide 24/7 professional support</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">Continuously innovate our solutions</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">Build long-term partnerships</span>
                </div>
              </div>
            </div>
            
            <div className="mt-12 lg:mt-0">
              <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8">
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                    <div className="text-2xl font-bold text-gray-900 mb-2">{mockStats.invoicesProcessed}</div>
                    <div className="text-gray-600">Invoices Processed</div>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                    <div className="text-2xl font-bold text-gray-900 mb-2">{mockStats.costSavingsGenerated}</div>
                    <div className="text-gray-600">Cost Savings</div>
                  </div>
                  <div className="text-center">
                    <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                    <div className="text-2xl font-bold text-gray-900 mb-2">{mockStats.clientRetentionRate}</div>
                    <div className="text-gray-600">Client Retention</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do for our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 text-center">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    {value.icon}
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900">
                    {value.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600">
              Experienced professionals dedicated to your success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2">
                <CardHeader className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-2xl">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900">
                    {member.name}
                  </CardTitle>
                  <CardDescription className="text-blue-600 font-medium">
                    {member.position}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-teal-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of companies that trust PBX Solutions to handle your transactional data and take your business to the next level. Get started with a free consultation with us today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
                Contact Us Today
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8">
                View Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;